import { useState } from 'react';
import { bondsApi } from '../services/api';
import type { Bond } from '../types/bonds';

export function useFileUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  const uploadFile = async (file: File, onDataReceived?: (bonds: Bond[], fileName: string) => void) => {
    // Reset states
    setUploadError(null);
    setUploadSuccess(null);
    setIsUploading(true);

    try {
      // Validate file type
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];

      if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.xls') && !file.name.toLowerCase().endsWith('.xlsx')) {
        throw new Error('Niewspierany format pliku. Obsługiwane formaty: .xls, .xlsx, .csv');
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('Plik jest za duży. Maksymalny rozmiar: 10MB');
      }

      const { bonds, fileName } = await bondsApi.uploadBondsFromExcel(file);

      // Call the callback with the new bonds data and fileName
      if (onDataReceived) {
        onDataReceived(bonds, fileName);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Wystąpił błąd podczas ładowania pliku';
      setUploadError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadState = () => {
    setUploadError(null);
    setUploadSuccess(null);
  };

  return {
    uploadFile,
    isUploading,
    uploadError,
    uploadSuccess,
    resetUploadState
  };
} 